import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck, faPlay } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Dialog from '@radix-ui/react-dialog';
import { Button, ProgressBar } from '@tryhackme/thm-ui-components';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sanitizeAndRenderHtmlString } from 'src/common/helpers';
import { useIsDarkMode } from 'src/common/hooks/use-is-dark-mode';
import type { PathOutlineRoom } from 'src/common/interfaces/paths';
import certificateImg from 'src/images/pngs/learningpathcert.png';
import { useGetPathOutlineQuery } from './path-outline-modal.slice';
import { ProgressBarAndLabelWrapper, ProgressBarLabel, ProgressBarWrapper, StyledCertificateImage, StyledCloseIconButton, StyledCompletedRoomIcon, StyledDescriptionAndCertificateColumn, StyledDialogContent, StyledDialogContentWrapper, StyledDialogFooter, StyledDialogOverlay, StyledLearningPathOverview, StyledModalContent, StyledModalHeader, StyledPathCard, StyledPathCardBanner, StyledPathCardContent, StyledPathContent, StyledPathIntro, StyledPathSection, StyledPathSectionItem, StyledPathSectionItemImageAndTitleWrapper, StyledPathTitle, StyledPlayIcon } from './path-outline-modal.styles';
import type { PathOutlineModalProps } from './path-outline.types';
export const PathOutlineModal = ({
  open,
  pathCode,
  handleModalAction: handleStartLearning,
  handleCloseButtonAction
}: PathOutlineModalProps) => {
  const [isScrollbarVisible, setIsScrollbarVisible] = useState<boolean>(false);
  const {
    data: pathData,
    isLoading
  } = useGetPathOutlineQuery(pathCode, {
    skip: !pathCode,
    refetchOnMountOrArgChange: true
  });
  const path = pathData?.data;
  const isDarkMode = useIsDarkMode();
  const navigate = useNavigate();
  const incompleteRoomCode = useMemo((): string | undefined => {
    const roomsProgressPercentages: Record<string, number> = {};
    const flattenedRooms = path?.modules?.flatMap(module => module.rooms) || [];
    for (const {
      code,
      progressPercentage
    } of flattenedRooms) {
      roomsProgressPercentages[code] = progressPercentage || 0;
    }
    const noRoomHasBeenStarted = Object.values(roomsProgressPercentages).every(percentage => percentage === 0);
    if (noRoomHasBeenStarted) {
      return undefined;
    }
    const [roomCode] = Object.entries(roomsProgressPercentages).find(([, progressPercentage]) => progressPercentage < 100) || [];
    return roomCode;
  }, [path?.modules]);
  const renderRoomCompletionComponent = ({
    progressPercentage
  }: PathOutlineRoom) => {
    const isRoomCompleted = progressPercentage === 100;
    const isRoomInProgress = progressPercentage !== undefined && progressPercentage > 0;
    if (isRoomCompleted) {
      return <StyledCompletedRoomIcon icon={faCircleCheck} data-testid="completed-room-icon" />;
    }
    if (isRoomInProgress) {
      const roundedProgressPercentage = Math.round(progressPercentage);
      return <ProgressBarAndLabelWrapper>
          <ProgressBarWrapper>
            <ProgressBar value={roundedProgressPercentage} height={10} radius={0.5} />
          </ProgressBarWrapper>
          <ProgressBarLabel data-testid="room-progress-bar-label">{`${roundedProgressPercentage}%`}</ProgressBarLabel>
        </ProgressBarAndLabelWrapper>;
    }
    return null;
  };
  const onMouseEnter = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (e.currentTarget.scrollHeight) {
      setIsScrollbarVisible(true);
    }
  };
  const onMouseLeave = () => {
    if (isScrollbarVisible) {
      setIsScrollbarVisible(false);
    }
  };
  const handleResumeLearning = useCallback(() => {
    handleCloseButtonAction?.();
    if (incompleteRoomCode && path) {
      navigate(`/path-action/${path.code}/join?redirect=/room/${incompleteRoomCode}?auto-join=true`);
    }
  }, [handleCloseButtonAction, incompleteRoomCode, navigate, path]);
  const renderModalCta = useCallback(() => <Button onClick={incompleteRoomCode ? handleResumeLearning : handleStartLearning}>
        {incompleteRoomCode && <StyledPlayIcon icon={faPlay} />}
        {incompleteRoomCode ? 'Resume learning' : 'Start learning'}
      </Button>, [handleResumeLearning, handleStartLearning, incompleteRoomCode]);
  if (isLoading || !path) return null;
  return <Dialog.Root open={open} data-sentry-element="unknown" data-sentry-component="PathOutlineModal" data-sentry-source-file="path-outline-modal.tsx">
      <Dialog.Portal data-sentry-element="unknown" data-sentry-source-file="path-outline-modal.tsx">
        <StyledDialogOverlay data-sentry-element="StyledDialogOverlay" data-sentry-source-file="path-outline-modal.tsx">
          <StyledDialogContent aria-describedby={undefined} onOpenAutoFocus={e => e.preventDefault()} data-sentry-element="StyledDialogContent" data-sentry-source-file="path-outline-modal.tsx">
            <StyledModalHeader data-sentry-element="StyledModalHeader" data-sentry-source-file="path-outline-modal.tsx">
              <StyledLearningPathOverview data-sentry-element="StyledLearningPathOverview" data-sentry-source-file="path-outline-modal.tsx">LEARNING PATH OVERVIEW</StyledLearningPathOverview>
              <StyledPathTitle data-sentry-element="StyledPathTitle" data-sentry-source-file="path-outline-modal.tsx">{path?.title}</StyledPathTitle>
            </StyledModalHeader>
            <StyledDialogContentWrapper data-sentry-element="StyledDialogContentWrapper" data-sentry-source-file="path-outline-modal.tsx">
              <StyledModalContent onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} data-sentry-element="StyledModalContent" data-sentry-source-file="path-outline-modal.tsx">
                <StyledDescriptionAndCertificateColumn data-sentry-element="StyledDescriptionAndCertificateColumn" data-sentry-source-file="path-outline-modal.tsx">
                  <StyledPathCard data-sentry-element="StyledPathCard" data-sentry-source-file="path-outline-modal.tsx">
                    <StyledPathCardBanner src={path?.secondaryImage ?? path?.image} alt="Path banner" data-sentry-element="StyledPathCardBanner" data-sentry-source-file="path-outline-modal.tsx" />
                    <StyledPathCardContent data-sentry-element="StyledPathCardContent" data-sentry-source-file="path-outline-modal.tsx">{sanitizeAndRenderHtmlString(path?.description)}</StyledPathCardContent>
                  </StyledPathCard>
                  <StyledPathCard data-sentry-element="StyledPathCard" data-sentry-source-file="path-outline-modal.tsx">
                    <StyledCertificateImage src={certificateImg} data-sentry-element="StyledCertificateImage" data-sentry-source-file="path-outline-modal.tsx" />
                    <StyledPathCardContent data-sentry-element="StyledPathCardContent" data-sentry-source-file="path-outline-modal.tsx">
                      <p>Complete this learning path and earn a certificate of completion</p>
                    </StyledPathCardContent>
                  </StyledPathCard>
                </StyledDescriptionAndCertificateColumn>
                <StyledPathContent data-sentry-element="StyledPathContent" data-sentry-source-file="path-outline-modal.tsx">
                  <StyledPathIntro data-sentry-element="StyledPathIntro" data-sentry-source-file="path-outline-modal.tsx">
                    <h3>Introduction</h3>
                    {sanitizeAndRenderHtmlString(path?.intro)}
                  </StyledPathIntro>
                  {path?.modules.map((module, index) => <StyledPathSection isDarkMode={isDarkMode} key={module.title}>
                      <h4>SECTION {index + 1}</h4>
                      <p>{module.title}</p>
                      {module.rooms.map(room => <StyledPathSectionItem key={room._id} $isScrollbarVisible={isScrollbarVisible}>
                          <StyledPathSectionItemImageAndTitleWrapper>
                            <img src={room.imageURL} alt={room.title} />
                            {room.title}
                          </StyledPathSectionItemImageAndTitleWrapper>
                          {renderRoomCompletionComponent(room)}
                        </StyledPathSectionItem>)}
                    </StyledPathSection>)}
                </StyledPathContent>
              </StyledModalContent>
            </StyledDialogContentWrapper>

            <StyledDialogFooter data-sentry-element="StyledDialogFooter" data-sentry-source-file="path-outline-modal.tsx">
              <Dialog.Close asChild data-sentry-element="unknown" data-sentry-source-file="path-outline-modal.tsx">{renderModalCta()}</Dialog.Close>
            </StyledDialogFooter>

            <StyledCloseIconButton aria-label="Close Modal" onClick={handleCloseButtonAction} data-sentry-element="StyledCloseIconButton" data-sentry-source-file="path-outline-modal.tsx">
              <FontAwesomeIcon icon={faXmark} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="path-outline-modal.tsx" />
            </StyledCloseIconButton>
          </StyledDialogContent>
        </StyledDialogOverlay>
      </Dialog.Portal>
    </Dialog.Root>;
};